// Modular local player-data layer. Swap these functions for a backend later
// without touching the UI.

const GAMES_KEY = "ac_games_v1";
const PLAYER_KEY = "ac_player_id";

export function getPlayerId() {
  let id = localStorage.getItem(PLAYER_KEY);
  if (!id) {
    id = "p_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem(PLAYER_KEY, id);
  }
  return id;
}

function readAll() {
  try {
    return JSON.parse(localStorage.getItem(GAMES_KEY) || "{}");
  } catch {
    return {};
  }
}

function writeAll(obj) {
  localStorage.setItem(GAMES_KEY, JSON.stringify(obj));
}

export function gameKey(mode, date) {
  return `${mode}:${date}`;
}

export function getGame(mode, date) {
  return readAll()[gameKey(mode, date)] || null;
}

export function saveGame(record) {
  const all = readAll();
  all[gameKey(record.mode, record.date)] = record;
  writeAll(all);
  return record;
}

// tiles: array of 5 entries 'correct' | 'incorrect' | 'unused'
export function buildTiles(status, guessesUsed) {
  const tiles = [];
  for (let i = 0; i < 5; i++) {
    if (status === "won") {
      if (i < guessesUsed - 1) tiles.push("incorrect");
      else if (i === guessesUsed - 1) tiles.push("correct");
      else tiles.push("unused");
    } else if (status === "lost") {
      tiles.push("incorrect");
    } else {
      // playing
      tiles.push(i < guessesUsed ? "incorrect" : "unused");
    }
  }
  return tiles;
}

export function scoreForGuess(guessNumber) {
  return { 1: 100, 2: 80, 3: 60, 4: 40, 5: 20 }[guessNumber] || 0;
}

// ---- stats derived from DAILY records only (time machine & dialogue tracked separately) ----
export function getDailyHistory() {
  const all = readAll();
  return Object.values(all)
    .filter((g) => g.mode === "daily" && (g.status === "won" || g.status === "lost"))
    .sort((a, b) => (a.date < b.date ? 1 : -1));
}

function computeStreaks(records) {
  const wonDates = new Set(records.filter((r) => r.status === "won").map((r) => r.date));
  const playedDates = new Set(records.map((r) => r.date));

  // longest streak: scan all won dates for consecutive runs
  const sortedWon = [...wonDates].sort();
  let longest = 0;
  let run = 0;
  let prev = null;
  for (const d of sortedWon) {
    if (prev && isNextDay(prev, d)) run += 1;
    else run = 1;
    longest = Math.max(longest, run);
    prev = d;
  }

  // current streak: count back from today (or yesterday if today unplayed)
  let cur = 0;
  let cursor = todayStr();
  if (!playedDates.has(cursor)) cursor = prevDay(cursor);
  while (wonDates.has(cursor)) {
    cur += 1;
    cursor = prevDay(cursor);
  }
  return { current: cur, longest };
}

function todayStr() {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  return fmt.format(new Date());
}
function toDate(s) {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}
function prevDay(s) {
  const dt = toDate(s);
  dt.setUTCDate(dt.getUTCDate() - 1);
  return dt.toISOString().slice(0, 10);
}
function isNextDay(a, b) {
  return prevDay(b) === a;
}

export function computeStats() {
  const records = getDailyHistory();
  const played = records.length;
  const wins = records.filter((r) => r.status === "won").length;
  const losses = played - wins;
  const totalScore = records.reduce((s, r) => s + (r.score || 0), 0);
  const totalGuesses = records.reduce(
    (s, r) => s + (r.status === "won" ? r.guessesUsed : 5),
    0
  );
  const { current, longest } = computeStreaks(records);
  return {
    played,
    wins,
    losses,
    winRate: played ? Math.round((wins / played) * 100) : 0,
    totalScore,
    avgScore: played ? Math.round(totalScore / played) : 0,
    avgGuesses: played ? (totalGuesses / played).toFixed(1) : "0.0",
    currentStreak: current,
    longestStreak: longest,
  };
}
